import React, { useState, useEffect, useRef, Component } from 'react';
import './theme.css';
import './App.css';
import Login from './components/Login';
import ProjectSelection from './components/ProjectSelection';
import LoginConfig from './components/LoginConfig';
import Dashboard from './components/Dashboard';
import UserManager from './components/UserManager';
import Settings from './components/Settings/Settings';
import ForcePasswordChangeDialog from './components/ForcePasswordChangeDialog';
import ErrorPage from './components/ErrorPage';
import API_CONFIG from './config/api';
import StratovaiLoader from './components/StratovaiLoader';
import { checkDebugMode } from './utils/debugMode';
import { ThemeProvider } from './theme/ThemeProvider';
import { reconcileSelectedProject } from './components/ProjectSwitcher/validateSelectedProject';

// Security configuration
const TOKEN_KEY = 'stratovai_access_token';
const REFRESH_TOKEN_KEY = 'stratovai_refresh_token';

// Secure API client with token management
class SecureAPIClient {
  constructor() {
    // Use relative URLs to go through the proxy instead of direct backend calls
    this.baseURL = '';
  }

  // Get stored tokens
  getAccessToken() {
    return localStorage.getItem(TOKEN_KEY);
  }

  getRefreshToken() {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  // Store tokens securely
  setTokens(accessToken, refreshToken) {
    localStorage.setItem(TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }

  // Clear tokens on logout
  clearTokens() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }

  // Check if token is expired
  isTokenExpired(token) {
    if (!token) {
      return true;
    }
    try {
      // Split the token and get the payload part
      const parts = token.split('.');
      if (parts.length !== 3) {
        return true;
      }
      
      // Decode the base64 payload
      const payload = JSON.parse(atob(parts[1]));
      
      // Check if token is expired (exp is in seconds, Date.now() is in milliseconds)
      const currentTime = Math.floor(Date.now() / 1000);
      const isExpired = payload.exp < currentTime;
      
      return isExpired;
    } catch (error) {
      return true;
    }
  }

  // Refresh access token
  async refreshAccessToken() {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      // Avoid refresh when there is no stored refresh token
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }
      const response = await fetch(API_CONFIG.buildUrl(API_CONFIG.ENDPOINTS.REFRESH_TOKEN), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Token refresh failed');
      }

      const data = await response.json();
      this.setTokens(data.access_token, refreshToken);
      return data.access_token;
    } catch (error) {
      this.clearTokens();
      throw error;
    }
  }

  // Make authenticated API request
  async makeRequest(url, options = {}) {
    let accessToken = this.getAccessToken();
    const requestUrl = API_CONFIG.buildUrl(url);

    // Check if token is expired and refresh if needed
    if (this.isTokenExpired(accessToken)) {
      try {
        accessToken = await this.refreshAccessToken();
      } catch (error) {
        // Clear tokens and stay on login page (don't redirect to landing page)
        this.clearTokens();
        throw error;
      }
    }

    // Add authorization header
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
      ...options.headers,
    };

    const parseJsonResponse = async (response) => {
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error(
          `Expected JSON from API but received ${response.status} (${contentType || 'unknown content type'}). Check API base URL configuration.`
        );
      }
      return response.json();
    };

    try {
      const response = await fetch(requestUrl, {
        ...options,
        headers,
      });

      if (response.status === 401) {
        // Token is invalid, try to refresh
        try {
          accessToken = await this.refreshAccessToken();
          headers.Authorization = `Bearer ${accessToken}`;
          
          const retryResponse = await fetch(requestUrl, {
            ...options,
            headers,
          });

          if (!retryResponse.ok) {
            throw new Error(`HTTP ${retryResponse.status}: ${retryResponse.statusText}`);
          }

          return await parseJsonResponse(retryResponse);
        } catch (refreshError) {
          // Refresh failed, clear tokens and stay on login page (don't redirect to landing page)
          this.clearTokens();
          localStorage.removeItem('authenticatedUser');
          // Don't redirect - let the app handle the login state
          throw new Error('Your session has expired. Please log in again.');
        }
      }

      if (!response.ok) {
        let errorBody = null;
        try {
          errorBody = await response.json();
        } catch {
          // ignore non-JSON error bodies
        }
        const detail = errorBody?.detail;
        const message = typeof detail === 'string'
          ? detail
          : (detail?.message || `HTTP ${response.status}: ${response.statusText}`);
        const err = new Error(message);
        err.status = response.status;
        err.detail = detail;
        throw err;
      }

      return await parseJsonResponse(response);
    } catch (error) {
      throw error;
    }
  }

  // Public API methods
  async login(email, password) {
    const loginUrl = API_CONFIG.buildUrl(API_CONFIG.ENDPOINTS.LOGIN);
    const response = await fetch(loginUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Login failed');
    }

    const data = await response.json();
    this.setTokens(data.access_token, data.refresh_token);
    return data.user;
  }

  async getProjects() {
    // Backend route: GET /projects returns projects for current user (from JWT)
    return this.makeRequest(API_CONFIG.buildUrl(API_CONFIG.ENDPOINTS.PROJECTS));
  }

  async getProjectSummary(projectId) {
    return this.makeRequest(API_CONFIG.buildUrl(API_CONFIG.ENDPOINTS.PROJECT_SUMMARY(projectId)));
  }

  async getLoginConfig(projectId) {
    return this.makeRequest(API_CONFIG.buildUrl(API_CONFIG.ENDPOINTS.PROJECT_LOGIN_CONFIG(projectId)));
  }

  async updateLoginConfig(projectId, config) {
    return this.makeRequest(API_CONFIG.buildUrl(API_CONFIG.ENDPOINTS.PROJECT_LOGIN_CONFIG(projectId)), {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  }

  async startUIExploration(userId, projectId, loginConfig) {
    return this.makeRequest(API_CONFIG.buildUrl(API_CONFIG.ENDPOINTS.EXPLORE_UI), {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        project_id: projectId,
        login_config: loginConfig,
      }),
    });
  }

  async getExplorationStatus(userId, projectId) {
    return this.makeRequest(API_CONFIG.buildUrl(API_CONFIG.ENDPOINTS.EXPLORE_UI_STATUS(userId, projectId)));
  }

  async getExplorationResults(projectId) {
    return this.makeRequest(API_CONFIG.buildUrl(API_CONFIG.ENDPOINTS.PROJECT_EXPLORATION_RESULTS(projectId)));
  }

  async getTestCases(projectId, status = null) {
    return this.makeRequest(API_CONFIG.buildUrl(API_CONFIG.ENDPOINTS.PROJECT_TEST_CASES(projectId, status)));
  }

  async updateTestCaseStatus(projectId, testCaseId, status) {
    return this.makeRequest(API_CONFIG.buildUrl(API_CONFIG.ENDPOINTS.PROJECT_TEST_CASE_STATUS(projectId, testCaseId)), {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  async deleteProjectTestCase(projectId, testCaseId) {
    return this.makeRequest(API_CONFIG.buildUrl(`/projects/${projectId}/test-cases/${testCaseId}`), {
      method: 'DELETE'
    });
  }

  // Automation v1 (Phase 2): analyze user-provided manual test cases for readiness.
  // Analysis only — no generation, no persistence.
  async analyzeManualTestCases(projectId, { content, source = 'paste', filename = null }) {
    return this.makeRequest(API_CONFIG.buildUrl('/automation/v1/manual-readiness'), {
      method: 'POST',
      body: JSON.stringify({
        project_id: Number(projectId),
        content,
        source,
        filename,
      }),
    });
  }

  async createAutomationGenerationJob(projectId, payload) {
    const sourceType = payload.source === 'txt_upload' ? 'txt_upload' : 'pasted_text';
    return this.makeRequest(API_CONFIG.buildUrl('/automation/v1/generation-jobs'), {
      method: 'POST',
      body: JSON.stringify({
        project_id: Number(projectId),
        source_type: sourceType,
        title: payload.title || null,
        case_index: payload.case_index ?? null,
        case_fingerprint: payload.case_fingerprint || null,
        raw_text: payload.content,
        clarification_resolutions: payload.clarification_resolutions || null,
      }),
    });
  }

  async getAutomationGenerationJob(jobId) {
    return this.makeRequest(API_CONFIG.buildUrl(`/automation/v1/generation-jobs/${jobId}`));
  }

  async discardAutomationGenerationJob(jobId) {
    return this.makeRequest(
      API_CONFIG.buildUrl(`/automation/v1/generation-jobs/${jobId}/discard`),
      { method: 'POST' }
    );
  }

  async createAutomationExecutionJob(generationJobId) {
    return this.makeRequest(
      API_CONFIG.buildUrl(`/automation/v1/generation-jobs/${generationJobId}/executions`),
      { method: 'POST' }
    );
  }

  async getAutomationRuntimeStatus(projectId) {
    return this.makeRequest(
      API_CONFIG.buildUrl(`/automation/v1/runtime-config/status?project_id=${Number(projectId)}`)
    );
  }

  async getAutomationFrameworkSettingsStatus(projectId) {
    return this.makeRequest(
      API_CONFIG.buildUrl(`/automation/v1/profile-settings/status?project_id=${Number(projectId)}`)
    );
  }

  async getAutomationExecutionJob(executionJobId) {
    return this.makeRequest(
      API_CONFIG.buildUrl(`/automation/v1/execution-jobs/${executionJobId}`)
    );
  }

  async listAutomationExecutionJobs(projectId, limit = 20) {
    return this.makeRequest(
      API_CONFIG.buildUrl(`/automation/v1/execution-jobs?project_id=${Number(projectId)}&limit=${limit}`)
    );
  }

  async rerunAutomationExecutionJob(executionJobId) {
    return this.makeRequest(
      API_CONFIG.buildUrl(`/automation/v1/execution-jobs/${executionJobId}/rerun`),
      { method: 'POST' }
    );
  }

  async discardAutomationExecutionJob(executionJobId) {
    return this.makeRequest(
      API_CONFIG.buildUrl(`/automation/v1/execution-jobs/${executionJobId}/discard`),
      { method: 'POST' }
    );
  }

  // Automation Cases (Phase 2.4A)
  async listAutomationCases(projectId, { includeObsolete = false, limit = 50 } = {}) {
    const params = new URLSearchParams({
      project_id: String(Number(projectId)),
      limit: String(limit),
      include_obsolete: includeObsolete ? 'true' : 'false',
    });
    return this.makeRequest(API_CONFIG.buildUrl(`/automation/v1/cases?${params}`));
  }

  async getAutomationCase(caseId) {
    return this.makeRequest(API_CONFIG.buildUrl(`/automation/v1/cases/${caseId}`));
  }

  async createAutomationCase(
    projectId,
    { sourceText, sourceType = 'paste', sourceFilename = null, title = null, analyze = true, forceDuplicate = false }
  ) {
    return this.makeRequest(API_CONFIG.buildUrl('/automation/v1/cases'), {
      method: 'POST',
      body: JSON.stringify({
        project_id: Number(projectId),
        source_type: sourceType,
        source_text: sourceText,
        source_filename: sourceFilename,
        title,
        analyze,
        force_duplicate: forceDuplicate,
      }),
    });
  }

  async analyzeAutomationCase(caseId) {
    return this.makeRequest(API_CONFIG.buildUrl(`/automation/v1/cases/${caseId}/analyze`), {
      method: 'POST',
    });
  }

  async approveAutomationCase(caseId) {
    return this.makeRequest(API_CONFIG.buildUrl(`/automation/v1/cases/${caseId}/approve`), {
      method: 'POST',
    });
  }

  async generateAutomationCase(caseId) {
    return this.makeRequest(API_CONFIG.buildUrl(`/automation/v1/cases/${caseId}/generate`), {
      method: 'POST',
    });
  }

  async runAutomationCase(caseId) {
    return this.makeRequest(API_CONFIG.buildUrl(`/automation/v1/cases/${caseId}/run`), {
      method: 'POST',
    });
  }

  async markAutomationCaseObsolete(caseId, reason = null) {
    return this.makeRequest(API_CONFIG.buildUrl(`/automation/v1/cases/${caseId}/mark-obsolete`), {
      method: 'POST',
      body: JSON.stringify(reason ? { reason } : {}),
    });
  }

  async getAutomationRepoConfig(projectId) {
    const params = new URLSearchParams({
      project_id: String(Number(projectId)),
    });
    return this.makeRequest(API_CONFIG.buildUrl(`/automation/v1/repo-config?${params}`));
  }

  async getAutomationKnowledgeLatestSnapshot(projectId, repoConfigId) {
    const params = new URLSearchParams({
      project_id: String(Number(projectId)),
      repo_config_id: repoConfigId,
    });
    return this.makeRequest(API_CONFIG.buildUrl(`/automation/v1/knowledge/latest-snapshot?${params}`));
  }

  async listAutomationKnowledgeJobs(projectId, { repoConfigId, limit = 10 } = {}) {
    const params = new URLSearchParams({
      project_id: String(Number(projectId)),
      limit: String(limit),
    });
    if (repoConfigId) {
      params.set('repo_config_id', repoConfigId);
    }
    return this.makeRequest(API_CONFIG.buildUrl(`/automation/v1/knowledge/refresh-jobs?${params}`));
  }

  async enqueueAutomationKnowledgeRefresh(projectId, { repoConfigId, scanKind }) {
    const params = new URLSearchParams({
      project_id: String(Number(projectId)),
    });
    return this.makeRequest(API_CONFIG.buildUrl(`/automation/v1/knowledge/refresh-jobs?${params}`), {
      method: 'POST',
      body: JSON.stringify({
        repo_config_id: repoConfigId,
        scan_kind: scanKind,
      }),
    });
  }

  async listAutomationKnowledgeManifest(
    projectId,
    snapshotId,
    { classification, status, limit = 50, offset = 0 } = {},
  ) {
    const params = new URLSearchParams({
      project_id: String(Number(projectId)),
      limit: String(limit),
      offset: String(offset),
    });
    if (classification) {
      params.set('classification', classification);
    }
    if (status) {
      params.set('status', status);
    }
    return this.makeRequest(
      API_CONFIG.buildUrl(`/automation/v1/knowledge/snapshots/${snapshotId}/manifest?${params}`),
    );
  }

  async listSafeSummaryEligibilityCandidates(projectId, filters = {}) {
    const params = new URLSearchParams({
      limit: String(filters.limit || 50),
      offset: String(filters.offset || 0),
    });
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, String(value));
      }
    });
    return this.makeRequest(
      API_CONFIG.buildUrl(`/api/projects/${Number(projectId)}/safe-summary-eligibility/candidates?${params}`),
      { method: 'GET' },
    );
  }

  async listSafeSummaryEligibilities(projectId, filters = {}) {
    const params = new URLSearchParams({
      limit: String(filters.limit || 50),
      offset: String(filters.offset || 0),
    });
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, String(value));
      }
    });
    return this.makeRequest(
      API_CONFIG.buildUrl(`/api/projects/${Number(projectId)}/safe-summary-eligibility?${params}`),
      { method: 'GET' },
    );
  }

  async listRepositorySourceConfigs(projectId) {
    return this.makeRequest(
      API_CONFIG.buildUrl(`/api/projects/${Number(projectId)}/repository-source/configs`),
      { method: 'GET' },
    );
  }

  async listRepositorySourcePurposeBindings(projectId, filters = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, String(value));
      }
    });
    const query = params.toString();
    const suffix = query ? `?${query}` : '';
    return this.makeRequest(
      API_CONFIG.buildUrl(`/api/projects/${Number(projectId)}/repository-source/purpose-bindings${suffix}`),
      { method: 'GET' },
    );
  }

  async bulkApproveTestCases(projectId, testCaseIds) {
    return this.makeRequest(API_CONFIG.buildUrl(`/test/${projectId}/test-cases/bulk-approve`), {
      method: 'POST',
      body: JSON.stringify({ test_case_ids: testCaseIds })
    });
  }

  // Phase 3: Embedding status API methods
  async getEmbeddingStatus(projectId) {
    return this.makeRequest(API_CONFIG.buildUrl(`/embeddings/${projectId}/status`));
  }

  async getRetrievalStatus(projectId) {
    return this.makeRequest(API_CONFIG.buildUrl(`/embeddings/${projectId}/retrieval-status`));
  }

  async approveTestCase(testCaseId, action, reason = null, projectId = null) {
    return this.makeRequest(API_CONFIG.buildUrl(API_CONFIG.ENDPOINTS.TEST_CASES_APPROVE), {
      method: 'POST',
      body: JSON.stringify({
        test_case_id: testCaseId,
        action: action,
        reason: reason,
        project_id: projectId
      }),
    });
  }

  async importExplorationResults(projectId, explorationResults) {
    return this.makeRequest(API_CONFIG.buildUrl(API_CONFIG.ENDPOINTS.TEST_CASES_IMPORT_EXPLORATION), {
      method: 'POST',
      body: JSON.stringify({
        project_id: projectId,
        exploration_results: explorationResults
      }),
    });
  }

  async updateTestCaseSource(testCaseId, newSource, reason = null, projectId = null) {
    return this.makeRequest(API_CONFIG.buildUrl(API_CONFIG.ENDPOINTS.TEST_CASES_UPDATE_SOURCE), {
      method: 'POST',
      body: JSON.stringify({
        test_case_id: testCaseId,
        new_source: newSource,
        reason: reason,
        project_id: projectId
      }),
    });
  }

  async getProjectTestCases(projectId, status = null) {
    const url = API_CONFIG.buildUrl(API_CONFIG.ENDPOINTS.PROJECT_TEST_CASES(projectId, status));
    return this.makeRequest(url, {
      method: 'GET'
    });
  }

  async generateAutomation(projectId, userId) {
    return this.makeRequest(API_CONFIG.buildUrl('/orchestrator/generate-automation'), {
      method: 'POST',
      body: JSON.stringify({
        project_id: projectId,
        user_id: userId
      }),
    });
  }

  async forceRefreshStatistics(projectId) {
    return this.makeRequest(API_CONFIG.buildUrl(`/projects/${projectId}/refresh-statistics`), {
      method: 'POST',
    });
  }

  async runAutomation(projectId, userId) {
    return this.makeRequest(API_CONFIG.buildUrl('/orchestrator/run-automation'), {
      method: 'POST',
      body: JSON.stringify({
        project_id: projectId,
        user_id: userId
      }),
    });
  }

  async refreshProjectStatistics(projectId) {
    return this.makeRequest(API_CONFIG.buildUrl(`/projects/${projectId}/refresh-statistics`), {
      method: 'POST'
    });
  }

  // Universal Learning Explorer methods
  async startUniversalExploration(userId, projectId, configDir = "config") {
    return this.makeRequest(API_CONFIG.buildUrl(API_CONFIG.ENDPOINTS.START_UNIVERSAL_EXPLORATION), {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        project_id: projectId,
        config_dir: configDir
      }),
    });
  }

  async getUniversalExplorationStatus(sessionId) {
    return this.makeRequest(API_CONFIG.buildUrl(API_CONFIG.ENDPOINTS.UNIVERSAL_EXPLORATION_STATUS(sessionId)), {
      method: 'GET',
    });
  }

  async getUniversalExplorationResults(sessionId) {
    return this.makeRequest(API_CONFIG.buildUrl(API_CONFIG.ENDPOINTS.UNIVERSAL_EXPLORATION_RESULTS(sessionId)), {
      method: 'GET',
    });
  }
}

// Create global API client instance
const apiClient = new SecureAPIClient();

// Error Boundary Component
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Uncaught error:', error, errorInfo);
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorPage
          error={{
            message: this.state.error?.message || 'An unexpected error occurred',
            details: this.state.errorInfo?.componentStack
          }}
          title="Application Error"
          message="Something went wrong. Please try refreshing the page or contact support if the problem persists."
          onRetry={this.handleReset}
          onGoHome={this.handleReset}
        />
      );
    }

    return this.props.children;
  }
}

function App() {
  // Initialize debug mode
  useEffect(() => {
    checkDebugMode();
  }, []);

  // Global error handler for uncaught errors
  useEffect(() => {
    const handleError = (event) => {
      console.error('Uncaught error:', event.error);
    };

    const handleUnhandledRejection = (event) => {
      console.error('Unhandled promise rejection:', event.reason);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  const [currentStep, setCurrentStep] = useState('login');
  const [authenticatedUser, setAuthenticatedUser] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loginConfig, setLoginConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForcePasswordChange, setShowForcePasswordChange] = useState(false);
  const [showProfilePasswordChange, setShowProfilePasswordChange] = useState(false);
  const authCheckCompleted = useRef(false);

  // Check for existing authentication on mount
  useEffect(() => {
    // Prevent multiple auth checks
    if (authCheckCompleted.current) return;
    
    const checkAuth = async () => {
      try {
        const accessToken = apiClient.getAccessToken();
        const refreshToken = apiClient.getRefreshToken();
        const storedUser = localStorage.getItem('authenticatedUser');
        const storedProject = localStorage.getItem('selectedProject');
        let storedLoginConfig = localStorage.getItem('loginConfig');

        // If we have a refresh token but no access token or expired access token, try to refresh
        if (refreshToken && (!accessToken || apiClient.isTokenExpired(accessToken))) {
          try {
            await apiClient.refreshAccessToken();
          } catch (refreshError) {
            // Clear session if refresh fails
            apiClient.clearTokens();
            localStorage.removeItem('authenticatedUser');
            localStorage.removeItem('selectedProject');
            localStorage.removeItem('loginConfig');
            setAuthenticatedUser(null);
            setSelectedProject(null);
            setLoginConfig(null);
            setCurrentStep('login');
            return;
          }
        }

        // Now check if we have a valid access token
        const currentAccessToken = apiClient.getAccessToken();
        if (currentAccessToken && !apiClient.isTokenExpired(currentAccessToken) && storedUser) {
          // User is authenticated, restore session
          const user = JSON.parse(storedUser);
          setAuthenticatedUser(user);
          
          if (storedProject) {
            const project = JSON.parse(storedProject);
            const projects = await apiClient.getProjects(user.user_id);
            const { project: validProject, nextStep } = reconcileSelectedProject(projects, project);

            if (nextStep === 'project-selection' || !validProject) {
              localStorage.removeItem('selectedProject');
              localStorage.removeItem('loginConfig');
              setSelectedProject(null);
              setLoginConfig(null);
              setCurrentStep('project-selection');
              return;
            }

            setSelectedProject(validProject);
            if (nextStep === 'replace') {
              localStorage.setItem('selectedProject', JSON.stringify(validProject));
              localStorage.removeItem('loginConfig');
              storedLoginConfig = null;
            }

            if (storedLoginConfig && nextStep === 'continue') {
              const config = JSON.parse(storedLoginConfig);
              setLoginConfig(config);
              setCurrentStep('dashboard');
            } else {
              try {
                const config = await apiClient.getLoginConfig(validProject.project_id);
                if (config && config.username && config.password) {
                  setLoginConfig(config);
                  localStorage.setItem('loginConfig', JSON.stringify(config));
                  setCurrentStep('dashboard');
                } else {
                  setCurrentStep('login-config');
                }
              } catch (error) {
                setCurrentStep('login-config');
              }
            }
          } else {
            // Check if user has multiple projects
            const projects = await apiClient.getProjects(user.user_id);
            if (projects && projects.length > 1) {
              setCurrentStep('project-selection');
            } else if (projects && projects.length === 1) {
              const project = projects[0];
              setSelectedProject(project);
              localStorage.setItem('selectedProject', JSON.stringify(project));
              
              // Check if login config exists
              try {
                const config = await apiClient.getLoginConfig(project.project_id);
                if (config && config.username && config.password) {
                  setLoginConfig(config);
                  localStorage.setItem('loginConfig', JSON.stringify(config));
                  setCurrentStep('dashboard');
                } else {
                  setCurrentStep('login-config');
                }
              } catch (error) {
                setCurrentStep('login-config');
              }
            } else {
              setCurrentStep('project-selection');
            }
          }
        } else {
          // Clear invalid session data
          apiClient.clearTokens();
          localStorage.removeItem('authenticatedUser');
          localStorage.removeItem('selectedProject');
          localStorage.removeItem('loginConfig');
          setAuthenticatedUser(null);
          setSelectedProject(null);
          setLoginConfig(null);
          setCurrentStep('login');
        }
      } catch (error) {
        // Clear session on error
        apiClient.clearTokens();
        localStorage.removeItem('authenticatedUser');
        localStorage.removeItem('selectedProject');
        localStorage.removeItem('loginConfig');
        setAuthenticatedUser(null);
        setSelectedProject(null);
        setLoginConfig(null);
        setCurrentStep('login');
      } finally {
        setLoading(false);
        authCheckCompleted.current = true;
      }
    };

    checkAuth();
  }, []);

  // Save authenticated user to localStorage whenever it changes
  useEffect(() => {
    if (authenticatedUser) {
      localStorage.setItem('authenticatedUser', JSON.stringify(authenticatedUser));
    } else {
      localStorage.removeItem('authenticatedUser');
    }
  }, [authenticatedUser]);

  useEffect(() => {
    if (selectedProject) {
      localStorage.setItem('selectedProject', JSON.stringify(selectedProject));
    } else {
      localStorage.removeItem('selectedProject');
    }
  }, [selectedProject]);

  useEffect(() => {
    if (loginConfig) {
      localStorage.setItem('loginConfig', JSON.stringify(loginConfig));
    } else {
      localStorage.removeItem('loginConfig');
    }
  }, [loginConfig]);

  // Enhanced login handler with security
  const handleLogin = async (user) => {
    setLoading(true);
    setError(null);
    
    try {
      setAuthenticatedUser(user);
      
      // Check if password change is required
      if (user.force_password_change) {
        setShowForcePasswordChange(true);
        setLoading(false);
        return;
      }
      
      // Fetch projects for user
      const projects = await apiClient.getProjects();
      
      if (Array.isArray(projects) && projects.length === 1) {
        const project = projects[0];
        
        // Fetch login config for the project
        const config = await apiClient.getLoginConfig(project.project_id);
        
        // Check if all required fields are present and non-empty
        const required = ['username', 'password', 'username_selector', 'password_selector', 'login_button_selector', 'success_indicator'];
        const isComplete = required.every(f => config[f] && config[f].trim() !== '');
        
        if (isComplete) {
          setSelectedProject(project);
          setLoginConfig(config);
          setCurrentStep('dashboard');
          return;
        } else {
          // Only show login config if user is admin and config is incomplete
          if (user.role === 'admin') {
            setSelectedProject(project);
            setCurrentStep('login-config');
            return;
          } else {
            // Non-admin users should still be able to access dashboard even with incomplete config
            setSelectedProject(project);
            setLoginConfig(config);
            setCurrentStep('dashboard');
            return;
          }
        }
      }
      
      setCurrentStep('project-selection');
    } catch (error) {
      setError(error.message);
      // Don't change step, let user retry
    } finally {
      setLoading(false);
    }
  };

  const handleProjectSelect = async (project) => {
    setSelectedProject(project);
    setLoading(true);
    setError(null);
    try {
      // Fetch login config for the selected project
      const config = await apiClient.getLoginConfig(project.project_id);
      setLoginConfig(config);
      // Check if all required fields are present and non-empty
      const required = ['username', 'password', 'username_selector', 'password_selector', 'login_button_selector', 'success_indicator'];
      const isComplete = required.every(f => config[f] && config[f].trim() !== '');
      if (isComplete) {
        setCurrentStep('dashboard');
      } else if (authenticatedUser && authenticatedUser.role === 'admin') {
        setCurrentStep('login-config');
      } else {
        // Non-admins go to dashboard even if config is incomplete
        setCurrentStep('dashboard');
      }
    } catch (error) {
      setLoginConfig(null);
      setError('Failed to load login configuration.');
      // Fallback: only admins can configure
      if (authenticatedUser && authenticatedUser.role === 'admin') {
        setCurrentStep('login-config');
      } else {
        setCurrentStep('dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleProjectSwitch = async (project) => {
    if (!project) return;

    const currentProjectId = selectedProject?.project_id ?? selectedProject?.id;
    const nextProjectId = project.project_id ?? project.id;
    if (
      currentProjectId != null &&
      nextProjectId != null &&
      String(currentProjectId) === String(nextProjectId)
    ) {
      return;
    }

    setError(null);
    setSelectedProject(project);
    try {
      const config = await apiClient.getLoginConfig(project.project_id);
      setLoginConfig(config);
    } catch (error) {
      setLoginConfig(null);
      setError('Failed to load login configuration for the selected project.');
    }
    setCurrentStep('dashboard');
  };

  const handleProjectReconcile = async (project, nextStep) => {
    if (nextStep === 'project-selection' || !project) {
      localStorage.removeItem('selectedProject');
      localStorage.removeItem('loginConfig');
      setSelectedProject(null);
      setLoginConfig(null);
      setCurrentStep('project-selection');
      return;
    }

    if (nextStep === 'replace') {
      await handleProjectSwitch(project);
    }
  };

  const handleLoginConfig = async (config) => {
    setLoading(true);
    setError(null);
    
    try {
      // Update login config on server
      await apiClient.updateLoginConfig(selectedProject.project_id, config);
      
      setLoginConfig(config);
      setCurrentStep('dashboard');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChanged = async () => {
    setShowForcePasswordChange(false);
    setLoading(true);
    
    try {
      // Fetch projects for user after password change
      const projects = await apiClient.getProjects();
      
      if (Array.isArray(projects) && projects.length === 1) {
        const project = projects[0];
        const config = await apiClient.getLoginConfig(project.project_id);
        const required = ['username', 'password', 'username_selector', 'password_selector', 'login_button_selector', 'success_indicator'];
        const isComplete = required.every(f => config[f] && config[f].trim() !== '');
        
        if (isComplete) {
          setSelectedProject(project);
          setLoginConfig(config);
          setCurrentStep('dashboard');
        } else {
          if (authenticatedUser.role === 'admin') {
            setSelectedProject(project);
            setCurrentStep('login-config');
          } else {
            setSelectedProject(project);
            setCurrentStep('dashboard');
          }
        }
      } else {
        setCurrentStep('project-selection');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenProfilePasswordChange = () => {
    setShowProfilePasswordChange(true);
  };

  const handleCloseProfilePasswordChange = () => {
    setShowProfilePasswordChange(false);
  };

  const handleProfilePasswordChanged = () => {
    setShowProfilePasswordChange(false);
    setAuthenticatedUser((prev) => (
      prev ? { ...prev, force_password_change: false } : prev
    ));
  };
  
  const handleNavigateToSettings = () => {
    setCurrentStep('settings');
  };

  const handleLogout = () => {
    // Clear all stored data
    apiClient.clearTokens();
    setAuthenticatedUser(null);
    setSelectedProject(null);
    setLoginConfig(null);
    setCurrentStep('login');
    setError(null);
    setShowForcePasswordChange(false);
    setShowProfilePasswordChange(false);
    
    // Clear all chat sessions
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('chat_session_')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    // Clear other localStorage items
    localStorage.removeItem('authenticatedUser');
    localStorage.removeItem('selectedProject');
    localStorage.removeItem('loginConfig');
  };

  const handleBackToProjectSelection = () => {
    setSelectedProject(null);
    setLoginConfig(null);
    setCurrentStep('project-selection');
  };

  const handleNavigateToUserManager = () => {
    setCurrentStep('user-manager');
  };

  const clearError = () => {
    setError(null);
  };

  if (loading) {
    return <StratovaiLoader />;
  }

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <div className="app">
          {error && (
            <div className="error-banner">
              <span>{error}</span>
              <button onClick={clearError} className="error-close">×</button>
            </div>
          )}
          
          {currentStep === 'login' && (
            <Login onLogin={handleLogin} apiClient={apiClient} />
          )}
          
          {currentStep === 'project-selection' && (
            <ProjectSelection 
              authenticatedUser={authenticatedUser}
              onProjectSelect={handleProjectSelect}
              onLogout={handleLogout}
              apiClient={apiClient}
            />
          )}
          
          {currentStep === 'login-config' && (
            <LoginConfig 
              selectedProject={selectedProject}
              onConfigComplete={handleLoginConfig}
              onBack={handleBackToProjectSelection}
              apiClient={apiClient}
            />
          )}
          
          {currentStep === 'dashboard' && (
            <Dashboard 
              authenticatedUser={authenticatedUser}
              selectedProject={selectedProject}
              loginConfig={loginConfig}
              onLogout={handleLogout}
              onBackToProjectSelection={handleBackToProjectSelection}
              onProjectSwitch={handleProjectSwitch}
              onProjectReconcile={handleProjectReconcile}
              onNavigateToUserManager={handleNavigateToUserManager}
              onNavigateToSettings={handleNavigateToSettings}
              onOpenChangePassword={handleOpenProfilePasswordChange}
              apiClient={apiClient}
            />
          )}

          {currentStep === 'settings' && (
            <Settings 
              authenticatedUser={authenticatedUser}
              onBack={() => setCurrentStep('dashboard')}
              onLogout={handleLogout}
            />
          )}
          
          {currentStep === 'user-manager' && (
            <UserManager 
              authenticatedUser={authenticatedUser}
            />
          )}

          {/* Force Password Change Dialog */}
          <ForcePasswordChangeDialog 
            open={showForcePasswordChange}
            onPasswordChanged={handlePasswordChanged}
            title="Change Your Password"
            description="For security reasons, you must change your password before continuing. Please choose a strong password."
            requireChange
            currentPasswordLabel="Current (Temporary) Password"
          />

          <ForcePasswordChangeDialog
            open={showProfilePasswordChange}
            onClose={handleCloseProfilePasswordChange}
            onPasswordChanged={handleProfilePasswordChanged}
            title="Change Password"
            description="Confirm your current password, then choose a new one."
            requireChange={false}
            currentPasswordLabel="Current Password"
          />
        </div>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App; 